import { MenuItem, Text } from "@chakra-ui/react";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useSubStatusTransition } from "src/hooks/useSubStatusTransition";
import { maybePluralize } from "src/services/format";
import * as GQL from "src/types/graphql";
import { FormSchoolStatus } from "../types";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction } from "./trackFormAction";
import { useEnqueueBulkSubstatus } from "./useEnqueueBulkSubstatus";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Excluding this item from consideration will prevent it from receiving offers.",
    "Excluding these items from consideration will prevent them from receiving offers."
  );

export const BulkExcludeForm = ({ selectedFormSchoolStatuses }: Props) => {
  const bulk = useBulkActionTargets();
  const enqueueBulkSubstatus = useEnqueueBulkSubstatus();
  const { isSubStatusTransitionAllowed } = useSubStatusTransition();

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Yes, exclude",
        colorScheme: "blue",
      },
      translate: true,
    });

  const handleOnClick = async () => {
    const count = bulk.selectionSize;
    trackFormAction({
      verb: "Clicked",
      actionType: "EXCLUDE_FROM_OFFERS",
      bulk: true,
      count,
      async: true,
    });

    setHeader(
      <Text>{`Exclude ${count} ${maybePluralize(count, "item")}?`}</Text>
    );
    setBody(<Text>{confirmationBodyText(count)}</Text>);
    if (!(await confirm())) {
      trackFormAction({
        verb: "Canceled",
        actionType: "EXCLUDE_FROM_OFFERS",
        bulk: true,
        count,
        async: true,
      });
      return;
    }

    trackFormAction({
      verb: "Confirmed",
      actionType: "EXCLUDE_FROM_OFFERS",
      bulk: true,
      count,
      async: true,
    });
    await enqueueBulkSubstatus({ action: "EXCLUDE" });
  };

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !selectedFormSchoolStatuses.every((result) =>
            isSubStatusTransitionAllowed(
              result.sub_status,
              GQL.form_school_rank_status_enum.NotConsidered
            )
          )
        }
      >
        Exclude
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
